import axios from 'axios';
import authService from './authService';

// Use same protocol as current page to avoid mixed content errors
const currentProtocol = window.location.protocol;
const currentHostname = window.location.hostname;

// Always use environment variable - no hardcoded URLs
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL environment variable is required');
}

const API = `${BACKEND_URL}/api`;

// Create axios instance with auth
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
    }
    return Promise.reject(error);
  }
);

export const proApiService = {
  // Projects
  async createProject(projectData) {
    const response = await apiClient.post('/projects', projectData);
    return response.data;
  },

  async getProjects(filters = {}) {
    const response = await apiClient.get('/projects', { params: filters });
    return response.data;
  },

  async getProject(projectId) {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  async updateProjectStatus(projectId, status) {
    const response = await apiClient.put(`/projects/${projectId}/status`, null, {
      params: { status }
    });
    return response.data;
  },

  async assignProjectManager(projectId, managerId) {
    const response = await apiClient.post(`/projects/${projectId}/assign-manager`, null, {
      params: { manager_id: managerId }
    });
    return response.data;
  },

  async addProjectPhase(projectId, phaseData) {
    const response = await apiClient.post(`/projects/${projectId}/phases`, phaseData);
    return response.data;
  },

  async assignWorkerToPhase(projectId, phaseId, workerData) {
    const response = await apiClient.post(`/projects/${projectId}/phases/${phaseId}/assign-worker`, workerData);
    return response.data;
  },

  async getProjectTimeline(projectId) {
    const response = await apiClient.get(`/projects/${projectId}/timeline`);
    return response.data;
  },

  async getProjectDashboard() {
    const response = await apiClient.get('/projects/dashboard/summary');
    return response.data;
  },

  // Communications
  async sendCommunication(commData) {
    const response = await apiClient.post('/communications', commData);
    return response.data;
  },

  async getCommunications(filters = {}) {
    const response = await apiClient.get('/communications', { params: filters });
    return response.data;
  },

  async sendProposal(projectId, clientEmail, proposalData) {
    const response = await apiClient.post('/communications/send-proposal', null, {
      params: { project_id: projectId, client_email: clientEmail },
      data: proposalData
    });
    return response.data;
  },

  async sendStatusUpdate(projectId, recipients, statusData) {
    const response = await apiClient.post('/communications/send-status-update', {
      project_id: projectId,
      recipients,
      ...statusData
    });
    return response.data;
  },

  async getEmailTemplates(type = null) {
    const params = type ? { communication_type: type } : {};
    const response = await apiClient.get('/communications/templates', { params });
    return response.data;
  },

  async createEmailTemplate(templateData) {
    const response = await apiClient.post('/communications/templates', templateData);
    return response.data;
  },

  async getCommunicationStats(communicationId) {
    const response = await apiClient.get(`/communications/${communicationId}/stats`);
    return response.data;
  },

  // Documents
  async createDocument(docData) {
    const response = await apiClient.post('/documents', docData);
    return response.data;
  },

  async getDocuments(filters = {}) {
    const response = await apiClient.get('/documents', { params: filters });
    return response.data;
  },

  async getDocument(documentId) {
    const response = await apiClient.get(`/documents/${documentId}`);
    return response.data;
  },

  async downloadDocument(documentId) {
    const response = await apiClient.get(`/documents/${documentId}/download`, {
      responseType: 'blob'
    });
    return response;
  },

  async sendForSignature(documentId, signatureData) {
    const response = await apiClient.post(`/documents/${documentId}/send-for-signature`, signatureData);
    return response.data;
  },

  async getSignatureStatus(documentId) {
    const response = await apiClient.get(`/documents/${documentId}/signature-status`);
    return response.data;
  },

  async regenerateDocument(documentId, updatedData) {
    const response = await apiClient.post(`/documents/regenerate/${documentId}`, updatedData);
    return response.data;
  },

  async getDocumentTemplates(type = null) {
    const params = type ? { document_type: type } : {};
    const response = await apiClient.get('/documents/templates', { params });
    return response.data;
  },

  // Disputes
  async createDispute(disputeData) {
    const response = await apiClient.post('/disputes', disputeData);
    return response.data;
  },

  async getDisputes(filters = {}) {
    const response = await apiClient.get('/disputes', { params: filters });
    return response.data;
  },

  async getDispute(disputeId) {
    const response = await apiClient.get(`/disputes/${disputeId}`);
    return response.data;
  },

  async updateDisputeStatus(disputeId, status, resolutionNotes = null, resolvedAmount = null) {
    const response = await apiClient.put(`/disputes/${disputeId}/status`, null, {
      params: { status, resolution_notes: resolutionNotes, resolved_amount: resolvedAmount }
    });
    return response.data;
  },

  async addDisputeDocument(disputeId, documentId) {
    const response = await apiClient.post(`/disputes/${disputeId}/add-document`, null, {
      params: { document_id: documentId }
    });
    return response.data;
  },

  async getDisputeTimeline(disputeId) {
    const response = await apiClient.get(`/disputes/${disputeId}/timeline`);
    return response.data;
  },

  async getDisputesDashboard() {
    const response = await apiClient.get('/disputes/dashboard/summary');
    return response.data;
  },

  // Dashboard & Analytics
  async getSystemStatus() {
    const response = await apiClient.get('/health');
    return response.data;
  }
};

export default proApiService;